<template>
  <div class="source-content panels">
    <div class="p-[0.625rem]">
      <search-filter
        :value="search"
        :options="sortedInteractiveTags"
        :selected="selectedTags"
        data-test="producer-assets-displayElements-searchFilter"
        @update:value="changeInteractiveSearch"
        @update:selected="changeInteractiveTags"
      />
    </div>

    <!-- No panels after searching -->
    <div v-if="!isLoading && !total" class="py-8 text-center">
      <h3 class="mb-4 text-[14px] font-semibold">
        No panels found
      </h3>

      <button
        class="tb-btn tb-btn-contained medium-em px-3"
        data-entity="producer-assets-resetPanelSearch"
        data-test="producer-assets-displayElements-resetSearch"
        @click="clearTags"
      >
        Reset Search
      </button>
    </div>

    <div
      ref="sourceList"
      class="source-list"
      data-test="producer-assets-displayElements-list"
    >
      <draggable
        :list="filteredPanels"
        @start="startDrag"
        @end="$store.dispatch('clearDragType')"
        :clone="clone"
        :sort="false"
        :animation="200"
        revert-on-spill="true"
        :group="{ name: 'timeline', pull: 'clone', put: false }"
      >
        <!-- Need both of the below classes for draggable logic in TimelineTheme -->
        <div
          v-for="panel in filteredPanels"
          :key="panel._id"
          class="source-item smart-panel"
          :data-template-id="panel._id"
          data-entity="producer-assets-panel"
          :data-test="`producer-assets-element:${panel._id}`"
        >
      <div class="tb-checkbox">
            <input
              type="checkbox"
              :id="panel._id"
              :value="panel"
              v-model="selected"
              data-entity="producer-assets-togglePanelSelection"
              :data-test="`producer-assets-element-checkBox:${panel._id}`"
            >

            <label :for="panel._id" />
          </div>

          <div class="source-item-wrapper">
            <img class="preview mr-2" :src="panel.preview_url"
              :data-test="`producer-assets-element-preview:${panel._id}`" />

            <div class="source-item-info-wrapper">
              <div class="source-title title-text" :title="panel.title"
                :data-test="`producer-assets-element-title:${panel._id}`">
                {{ panel.title }}
              </div>

              <div
                :class="{
                  'source-item-tags': true,
                  'mt-1': panel.tags?.length,
                }"
                :data-test="`producer-assets-element-tags:${panel._id}`"
              >
                <span
                  v-for="tag in displayTags(getUniqueTags(panel.tags))"
                  :key="tag"
                  class="tag"
                  :data-test="`producer-assets-element-tags:${panel._id}_${tag}`"
                >
                  {{ tag }}
                </span>
                <tag-overflow-popover
                  v-if="tagOverflowCount(getUniqueTags(panel.tags))"
                  :tags="getUniqueTags(panel.tags)"
                  :data-test="`producer-assets-element-tags:${panel._id}_overflow`"
                />
              </div>
            </div>

            <div class="source-description ml-1">
              <div class="description-text capitalize"
                :data-test="`producer-assets-element-type:${panel._id}`">
                {{ displayTitle(panel) }}
              </div>
            </div>

          <div class="source-actions ml-1">
              <button
                class="tb-btn tb-btn-contained sm medium-em add-to-playlist"
                title="Add to playlist"
                data-entity="producer-assets-addPanelToPlaylist"
                :data-test="`producer-assets-element-addToPlaylist:${panel._id}`"
                @click="addToPlaylist(panel, 'Panel')"
              >
                <span class="fas fa-plus" />
              </button>

              <!-- CAN WE REMOVE THIS CONTEXT MENU? WHY ARE PANELS SO SPECIAL? -->
              <dropdown position="left" class="more-actions ml-1">
                <template #trigger>
                  <button
                    class="tb-btn tb-btn-text sm medium-em"
                    title="More actions"
                    data-entity="producer-assets-togglePanelContextMenu"
                    :data-test="`producer-assets-element-context:${panel._id}`"
                  >
                    <i class="fas fa-ellipsis-v"></i>
                  </button>
                </template>

                <template #dropdown-body>
                  <button
                    v-if="panel.type !== 'widget'"
                    class="dropdown-list-item"
                    data-entity="producer-assets-editPanel"
                    @click="openEditModal(panel)"
                  >
                    <span class="dropdown-list-item-label">Edit</span>
                  </button>

                  <button
                    v-if="panel.type !== 'widget'"
                    class="dropdown-list-item"
                    data-entity="producer-assets-duplicatePanel"
                    @click="duplicatePanel(panel._id)"
                  >
                    <span class="dropdown-list-item-label">Duplicate</span>
                  </button>
                </template>
              </dropdown>
            </div>
          </div>
        </div>
      </draggable>

      <div ref="loadTrigger" class="load-trigger flex min-h-16 w-full items-center justify-center">
        <div v-if="isLoading" class="loading loading--inline m-0" data-test="producer-assets-displayElements-loading">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
      </div>
    </div>

    <div
      v-if="selected.length"
      class="producer-assets-selected flex items-center justify-end p-4"
      data-test="producer-assets-selected"
    >
      <p class="producer-assets-selected__count mb-0 mr-4 mt-0 text-primary" data-test="producer-assets-selected-count">{{ selected.length }} selected</p>

      <button
        class="tb-btn tb-btn-contained medium-em ml-4"
        :class="{'disabled': !selected.length }"
        :disabled="!selected.length"
        data-entity="producer-assets-addSelectedPanelsToPlaylist"
        data-test="producer-assets-selected-addToPlaylist"
        @click="addSelectedToPlaylist('Panels')"
      >
        Add to playlist
      </button>
    </div>


    <modal v-if="showPanelEditor" class="edit-smart-panel-modal" @close="showPanelEditor = false">
      <template #header>
        <div class="title">Edit Smart Panel</div>
        <button
          class="modal-close tb-btn tb-btn-text sm low-em"
          data-entity="producer-assets-closePanelEditor"
          data-test="producer-assets-closeInteractiveEditor"
          @click="showPanelEditor = false"
        >
          <span class="fas fa-times" />
        </button>
      </template>

      <iframe border="0" width="100%" height="100%" v-bind:src="iframeSrc" />
    </modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { pick } from 'lodash-es';

import SearchFilter from '@/js/shared/SearchFilter.vue';
import TagOverflowPopover from '@/js/shared/TagOverflowPopover.vue';
import tabMixins from '../../mixins/tab-mixins';
import ModalComponent from '../../components/modal/Modal.vue';

const { v1: uuidv1 } = require('uuid'); // Dis da new way

export default {
  mixins: [tabMixins],
  components: {
    modal: ModalComponent,
    SearchFilter,
    TagOverflowPopover,
  },

  data() {
    return {
      showAlert: false,
      showPanelEditor: false,
      selectedPanel: null,
    };
  },

  created() {
    window.addEventListener('message', this.onMessage, false);
    this.$store.dispatch('getPanels');
    this.getInteractiveTags();
  },

  mounted() {
    if (this.$refs.sourceList) {
      this.intersectionObserver = new IntersectionObserver(this.loadMore, {
        root: this.$refs.sourceList,
      });

      this.intersectionObserver.observe(this.$refs.loadTrigger);
    }
  },

  watch: {
    panels() {
      if (!this.$refs.sourceList || !this.panels.length) {
        return;
      }

      if (this.$refs.sourceList.scrollHeight <= this.$refs.sourceList.offsetHeight) {
        // List isn't scrollable, so try to load more right away
        // Otherwise the intersection observer will never trigger it's callback
        this.loadMore();
      }
    },
  },

  beforeUnmount() {
    window.removeEventListener('message', this.onMessage, false);
  },

  computed: {
    ...mapState({
      selectedTags: (state) => state.panels.tags,
      search: (state) => state.panels.search,
      panels: (state) => state.panels.panels,
      isLoading: (state) => state.panels.isLoading,
      owner: (state) => state.production.production.owner,
      total: (state) => state.panels.total,
      sortedInteractiveTags: (state) => state.panels.interactiveTags,
    }),

    iframeSrc() {
      return `${process.env.TAGBOARD_PANELS_URL}/settings?user_id=${this.owner}&src=producer#/${this.selectedPanel._id}/step/general`;
    },
    allValidPanels() {
      const excludedTypes = ['leaderboard', 'hashtag-meter', 'youtube', 'vimeo', 'hashtag-battle', 'cta'];
      return this.panels.filter(panel => !excludedTypes.includes(panel.type));
    },
    filteredPanels() {
      return this.allValidPanels;
    },
  },

  methods: {
    ...mapActions([
      'getInteractiveTags',
      'changeInteractiveSearch',
      'changeInteractiveTags',
      'resetInteractiveSearch',
    ]),

    clearTags() {
      this.resetInteractiveSearch();
    },

    getUniqueTags(tags = []) {
      return [...new Set(tags)];
    },

    displayTags(tags = []) {
      return tags.slice(0, 2);
    },

    tagOverflowCount(tags = []) {
      return Math.max((tags?.length || 0) - 2, 0);
    },

    /**
    * Attempt to load more smart panels
    */
    loadMore() {
      if (!this.isLoading && this.$store.getters.hasMoreSmartPanels) {
        this.$store.dispatch('incrementPanelsPage');
      }
    },

    onMessage(e) {
      if (e.origin !== process.env.TAGBOARD_PANELS_URL) {
        return;
      }

      if (typeof e.data === 'string' && e.data.indexOf('tgbSmartPanelUpdate') === 0) {
        const panel = JSON.parse(e.data.split('|')[1]);
        this.$store.commit('EDIT_PANEL', panel);
      }
    },

    startDrag(e) {
      const { item } = e;
      const { templateId } = item.dataset;

      const grabGroup = !!this.selected.find((t) => t._id === templateId);
      this.$store.dispatch('setDragType', {
        type: 'panel',
        group: grabGroup ? this.selected.map(this.clone) : null,
      });
    },

    displayTitle(panel) {
      // Allow pretty names for multi worded panels types
      switch (panel.type) {
        case 'hashtag-battle':
          return 'hashtag poll';
        case 'twitter-poll':
          return 'twitter poll';
        case 'reddit-poll':
          return 'reddit poll';
        case 'multiple-choice-poll':
          return 'Poll';
        case 'poll-results':
          return 'Poll Results';
        default:
          return panel.type;
      }
    },

    openEditModal(panel) {
      this.selectedPanel = panel;
      this.showPanelEditor = true;
    },

    duplicatePanel(panelId) {
      this.$store.dispatch('duplicatePanel', panelId);
    },

    clone(original) {
      const clone = pick(original, ['_id', 'type', 'title']);
      clone.timeline_id = uuidv1();
      clone.displayTime = 5000;
      clone.transition = 'Cut'; // legacy but keep for backwards compatibility during testing
      clone.layout = 'panel';
      clone.posts = [{ timeline_id: clone.timeline_id }];
      return clone;
    },
  },
};
</script>
